import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { ORDER_PRICE_RANGE_MULTIPLIER } from "@shared/features/tradingTerminal/common/constants";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { USD_ALTERNATIVE_PAIRS } from "@shared/features/bots/constants";
import { useClientsData } from "@shared/utils/hooks/misc";
import { sortByStringProperty } from "@shared/utils/general";
import { ExchangeTag } from "@shared/types";
import { findKey, pickBy } from "lodash";
import { EXCHANGE_TO_PRIMARY_EXCHANGE } from "@shared/common/constants";

const ExchangesMetadataProvider = ({ children }: PropsWithChildren) => {
  const getExchangesMetadataStatus =
    sharedLib.sharedApi.common.useGetExchangesMetadataQuery(undefined);
  const getExchangesListStatus =
    sharedLib.sharedApi.configuration.useGetExchangesListQuery({
      nested_exchanges: true,
    });
  const { data: exchangesList } = getExchangesListStatus;
  const { data: rawExchangesMetadata } = getExchangesMetadataStatus;
  const isSuccess =
    getExchangesListStatus.isSuccess && getExchangesMetadataStatus.isSuccess;
  const {
    getAccountById,
    getIsMicaAccount,
    isSuccess: isClientsDataSuccess,
  } = useClientsData();

  const exchangesMetadata = useMemo(() => {
    if (exchangesList) {
      const exchangesSet = new Set(
        exchangesList?.map((exchange) => exchange.exchange)
      );
      return pickBy(rawExchangesMetadata, (_, exchange) =>
        exchangesSet.has(exchange)
      );
    }
  }, [rawExchangesMetadata, exchangesList]);

  const getExchangeMetadata = useCallback(
    (exchange?: string) => {
      return exchange ? exchangesMetadata?.[exchange] : undefined;
    },
    [exchangesMetadata]
  );

  const getExchangePriceFactorLimits = useCallback(
    (exchange?: string) =>
      getExchangeMetadata(exchange)?.limits?.price_factor ?? {
        min: ORDER_PRICE_RANGE_MULTIPLIER,
        max: ORDER_PRICE_RANGE_MULTIPLIER,
      },
    [getExchangeMetadata]
  );

  const getAlternativeUsdPairs = useCallback(
    (accountId?: number) => {
      const account = getAccountById(accountId);
      const exchangeMetadata = getExchangeMetadata(account?.exchange);
      const pairs = getIsMicaAccount(accountId)
        ? exchangeMetadata?.mica_pairs
        : exchangeMetadata?.pairs;
      return pairs?.filter((pair) => USD_ALTERNATIVE_PAIRS.includes(pair));
    },
    [getAccountById, getExchangeMetadata, getIsMicaAccount]
  );

  const exchanges = useMemo(() => {
    return Object.keys(exchangesMetadata || {}).sort(sortByStringProperty);
  }, [exchangesMetadata]);

  const exchangeToDisplayName = useMemo(() => {
    const map: Record<string, string> = {};
    exchangesList?.forEach((info) => {
      map[info.exchange] = info.exchange_display;
    });
    return map;
  }, [exchangesList]);

  const exchangeToIsFree = useMemo(() => {
    const map: Record<string, boolean> = {};
    exchangesList?.forEach((info) => {
      map[info.exchange] = Boolean(info.is_free);
    });
    return map;
  }, [exchangesList]);

  const getIsFree = useCallback(
    (exchange?: string) => getExchangeIsFree(exchangeToIsFree, exchange),
    [exchangeToIsFree]
  );

  const getExchangeTag = useCallback(
    (exchange?: string) => {
      const metadata = getExchangeMetadata(exchange);
      if (metadata?.is_margin_exchange && !metadata?.is_inverse_exchange)
        return ExchangeTag.FUTURES;
      if (metadata?.is_inverse_exchange) return ExchangeTag.INVERSE;
      if (metadata?.is_spot_exchange) return ExchangeTag.SPOT;
    },
    [getExchangeMetadata]
  );

  const finalGetExchangesMetadataStatus = useMemo(() => {
    return {
      ...getExchangesMetadataStatus,
      isLoading:
        getExchangesMetadataStatus.isLoading ||
        getExchangesListStatus.isLoading,
      isSuccess,
    };
  }, [getExchangesMetadataStatus, isSuccess, getExchangesListStatus]);

  const contextValue = useMemo(
    () => ({
      exchangesMetadata,
      getExchangesMetadataStatus: finalGetExchangesMetadataStatus,
      getExchangeMetadata,
      getExchangePriceFactorLimits,
      isSuccess,
      getAlternativeUsdPairs,
      exchanges,
      getExchangeTag,
      exchangeToDisplayName,
      getIsFree,
    }),
    [
      exchangesMetadata,
      finalGetExchangesMetadataStatus,
      getExchangeMetadata,
      getExchangePriceFactorLimits,
      isSuccess,
      isClientsDataSuccess,
      getAlternativeUsdPairs,
      exchanges,
      getExchangeTag,
      exchangeToDisplayName,
      getIsFree,
    ]
  );

  return (
    <ExchangesMetadataContext.Provider value={contextValue}>
      {children}
    </ExchangesMetadataContext.Provider>
  );
};

export default React.memo(ExchangesMetadataProvider);

export const getExchangeIsFree = (
  exchangeToIsFree: Record<string, boolean>,
  exchange?: string
) => {
  if (!exchange) return false;

  if (Object.prototype.hasOwnProperty.call(exchangeToIsFree, exchange)) {
    return exchangeToIsFree[exchange];
  }

  const exchangeMatchKey = findKey(
    EXCHANGE_TO_PRIMARY_EXCHANGE,
    (primaryExchange, exchangeName) =>
      primaryExchange === exchange &&
      Object.prototype.hasOwnProperty.call(exchangeToIsFree, exchangeName)
  );

  return exchangeMatchKey ? exchangeToIsFree[exchangeMatchKey] : false;
};
